import React from 'react';
import { Mail, Phone } from 'lucide-react';
import { BaseQuestion, shouldShowAnswerOptions } from './types';
import { QuestionFooter } from './QuestionHelpers';

interface TextQuestionProps {
  question: BaseQuestion;
  value: string;
  onChange: (value: string) => void;
  onBlur: () => void;
  error?: string;
  primaryColor: string;
  type?: string;
  moreInfo?: string;
  isMoreInfoExpanded?: boolean;
  onToggleMoreInfo?: () => void;
}

export const TextQuestion: React.FC<TextQuestionProps> = ({
  question,
  value,
  onChange,
  onBlur,
  error,
  primaryColor,
  type = 'text',
  moreInfo,
  isMoreInfoExpanded,
  onToggleMoreInfo,
}) => {
  const characterLimit = question.options?.useCharacterLimit === "1"
    ? (question.options?.characterLimit || 0)
    : 0;
  const characterMinimum = question.options?.useCharacterMinimum === "1"
    ? (question.options?.characterMinimum || 0)
    : 0;
  const currentLength = (value || '').length;

  const getInputType = () => {
    switch (type) {
      case 'email': return 'email';
      case 'phone': return 'tel';
      case 'number': return 'number';
      case 'date': return 'date';
      default: return 'text';
    }
  };

  const renderIcon = () => {
    switch (type) {
      case 'email':
        return <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />;
      case 'phone':
        return <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />;
      default:
        return null;
    }
  };

  const getPlaceholder = () => {
    switch (type) {
      case 'email': return 'you@example.com';
      case 'phone': return '(123) 456-7890';
      default: return '';
    }
  };

  const hasIcon = type === 'email' || type === 'phone';

  return (
    <div>
      <div className={hasIcon ? 'relative' : ''}>
        {renderIcon()}
        <input
          type={getInputType()}
          id={question.id}
          placeholder={getPlaceholder()}
          value={value || ''}
          onChange={(e) => {
            // Enforce character limit if enabled
            if (characterLimit > 0 && e.target.value.length > characterLimit) {
              return;
            }
            onChange(e.target.value);
          }}
          onBlur={onBlur}
          className={`w-full border rounded-lg p-3 text-sm focus:ring-2 focus:border-transparent ${
            error ? 'border-red-500' : 'border-gray-200'
          } ${hasIcon ? 'pl-10' : ''}`}
          style={{ '--tw-ring-color': error ? '#EF4444' : primaryColor } as React.CSSProperties}
          maxLength={characterLimit > 0 ? characterLimit : undefined}
        />
      </div>

      <QuestionFooter
        error={error}
        characterLimit={characterLimit}
        characterMinimum={characterMinimum}
        currentLength={currentLength}
        helpText={question.description}
        moreInfo={moreInfo}
        isMoreInfoExpanded={isMoreInfoExpanded}
        onToggleMoreInfo={onToggleMoreInfo}
      />
    </div>
  );
};